import { useMercure } from '@/composables/useMercure';
import { usePermissionsStore } from '@/stores/permissionsStore';
import { useUserStore } from '@/stores/user';
import { fetchEffectivePermissions } from '@/composables/usePermissionsApi';
import * as Sentry from '@sentry/vue';

/**
 * Manages the Mercure SSE subscription for the current user's permissions topic.
 *
 * DEVAMS-2014 design decisions:
 * - Topic format is `user-{userId}-permissions` to match the BE publisher
 *   (PermissionMercurePublisher) and the project-wide Mercure naming convention
 *   (e.g. `user-{id}-notification`, `station-{code}-flights`).
 * - Wraps `useMercure` rather than rolling its own EventSource, keeping Capacitor
 *   app-pause/resume handling consistent across all SSE subscriptions.
 * - Token is sourced from permissionsStore.mercureToken (set when fetchPermissions
 *   captures X-Mercure-Token from the /me/permissions response).
 * - On any Mercure message (the BE publishes `{ at: <ISO8601> }`), the store is
 *   re-fetched — no interpretation of the payload needed.
 * - onTokenExpired re-fetches /me/permissions to obtain a fresh subscriber JWT,
 *   matching the pattern used by plannedWorkStore / notificationStore.
 *
 * Returns `{ start(), stop() }`. Caller is responsible for lifecycle
 * (call start() after auth resolves; stop() on logout).
 */
export function usePermissionsMercure(): { start: () => void; stop: () => void } {
    const permissionsStore = usePermissionsStore();
    const userStore = useUserStore();

    let handle: ReturnType<typeof useMercure> | null = null;

    function buildTopic(): string {
        const userId = userStore.getUserInfo?.id ?? 0;
        return `user-${userId}-permissions`;
    }

    async function refreshToken(): Promise<string | void> {
        try {
            const { data, mercureToken, mercureTopic } = await fetchEffectivePermissions();
            // Update the store with the refreshed grants and headers
            permissionsStore.grants = data.grants;
            permissionsStore.loaded = true;
            permissionsStore.lastFetchedAt = Date.now();
            permissionsStore.lastError = null;
            if (mercureToken) permissionsStore.mercureToken = mercureToken;
            if (mercureTopic) permissionsStore.mercureTopic = mercureTopic;
            return mercureToken ?? undefined;
        } catch (error: unknown) {
            Sentry.captureException(error, {
                tags: { feature: 'permissions-mercure', action: 'token-refresh' },
            });
            return undefined;
        }
    }

    function start(): void {
        const token = permissionsStore.mercureToken;
        if (!token) {
            console.warn('[usePermissionsMercure] No Mercure token available — skipping subscription. Will rely on focus-based staleness refresh.');
            return;
        }

        handle = useMercure({
            topic: buildTopic(),
            token,
            onMessage: () => {
                // The BE publishes { at: <ISO8601> } on any permission change.
                // Re-fetch the full effective permissions rather than trying to
                // apply incremental patches — keeps the logic simple and correct.
                permissionsStore.fetchPermissions();
            },
            onTokenExpired: refreshToken,
        });

        handle.connect();
    }

    function stop(): void {
        handle?.disconnect();
        handle = null;
    }

    return { start, stop };
}
